import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Date,
  Dropdown,
  DropdownMenuItem,
  Icon,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Textarea,
  UtilityInlineGrid,
  UtilityList,
  UtilityListItem,
} from 'rhinostyle';
import moment from 'moment-timezone';
import { DATE_FORMAT_DEFAULT } from '../constants/DateConstants';
import { VCARD_FILE_TYPE, iconTypeVCard } from '../constants/AppConstants';
import { FORM_VIEW } from '../constants/UserPermissionsConstants';
import { formatPhone } from '../helpers/PhoneHelpers';
import { formatFilename } from '../helpers/StringHelpers';
import { handleAddFile as uploadFile } from '../helpers/AttachmentHelpers';
import { getLoggedInUserPermissionNames } from '../selectors/userSelectors';
import { getCurrentOrg } from '../selectors/organizationSelectors';
import FormLibraryModal from './FormLibraryModal';

function getAttachmentIcon(attachment) {
  if (attachment.formId) return 'signature';
  if (attachment.type === VCARD_FILE_TYPE) return iconTypeVCard;
  return 'attachment';
}

function parseDateAndTime(isoString, timezone) {
  const m = moment.utc(isoString).tz(timezone);
  return {
    date: m.format(DATE_FORMAT_DEFAULT),
    time: m.format('HH:mm'),
  };
}

function buildTimeOptions() {
  const options = [];
  for (let h = 0; h < 24; h += 1) {
    for (let m = 0; m < 60; m += 15) {
      const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const label = moment(`2000-01-01T${val}`).format('h:mm A');
      options.push({ value: val, label });
    }
  }
  return options;
}

const TIME_OPTIONS = buildTimeOptions();

const ScheduledMessageModal = (props) => {
  const {
    isOpen, onClose, onConfirm, initialScheduledAt, initialMessageText, phones, selectedPhoneId,
    attachments, onRemoveAttachment, onAddAttachments,
    isRhinoformEnabled, isOfficeVCardEnabled,
  } = props;
  const currentAttachments = attachments || [];

  const uploaderInputRef = useRef();
  const [formLibraryModalOpen, setFormLibraryModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const userTimezone = moment.tz.guess();
  const tzLabel = `${moment.tz(userTimezone).format('z')} (${userTimezone.replace(/_/g, ' ')})`;

  const today = moment.tz(userTimezone);
  const todayStr = today.format(DATE_FORMAT_DEFAULT);

  const isEditMode = !!initialScheduledAt;
  const initial = isEditMode ? parseDateAndTime(initialScheduledAt, userTimezone) : null;

  const [selectedDate, setSelectedDate] = useState(initial ? initial.date : todayStr);
  const [selectedTime, setSelectedTime] = useState(initial ? initial.time : '');
  const [messageText, setMessageText] = useState(initialMessageText || '');
  const [phoneId, setPhoneId] = useState(selectedPhoneId != null ? Number(selectedPhoneId) : null);
  const [dateError, setDateError] = useState(null);

  useEffect(() => {
    if (isOpen && !isEditMode) {
      setSelectedDate(todayStr);
      setSelectedTime('');
      setDateError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (phones && phones.length > 0 && phoneId && !phones.some((p) => Number(p.id) === phoneId)) {
      setPhoneId(null);
    }
  }, [phones]);

  const isToday = selectedDate === todayStr;
  const nowTimeStr = today.format('HH:mm');
  const availableTimeOptions = isToday
    ? TIME_OPTIONS.filter((o) => o.value > nowTimeStr)
    : TIME_OPTIONS;

  function handleDateChange(date) {
    const newDate = moment(date).format(DATE_FORMAT_DEFAULT);
    setSelectedDate(newDate);
    setDateError(null);
    if (selectedTime && newDate === todayStr && selectedTime <= nowTimeStr) {
      setSelectedTime('');
    }
  }

  function handleTimeSelect(option) {
    setSelectedTime(option.value);
    setDateError(null);
  }

  function handleConfirm() {
    if (!selectedDate || !selectedTime) return;
    const hasContent = messageText.trim() || currentAttachments.length > 0;
    if (isEditMode && !hasContent) return;
    const scheduledMoment = moment.tz(`${selectedDate}T${selectedTime}`, userTimezone);
    if (!scheduledMoment.isAfter(moment())) {
      setDateError('Past date not allowed');
      return;
    }
    onConfirm(scheduledMoment.utc().toISOString(), messageText.trim(), phoneId);
  }

  function handleFormAppend(selectedForms) {
    setFormLibraryModalOpen(false);
    if (!onAddAttachments) return;
    const nonFormAttachments = currentAttachments.filter((a) => !a.formId);
    const trimmedForms = selectedForms.map((f) => ({ formId: f.attachmentUrl, title: f.name }));
    onAddAttachments([...nonFormAttachments, ...trimmedForms]);
  }

  function handleVCardAppend(vCards) {
    setFormLibraryModalOpen(false);
    if (!onAddAttachments) return;
    const nonVCardAttachments = currentAttachments.filter((a) => a.type !== VCARD_FILE_TYPE);
    onAddAttachments([...nonVCardAttachments, ...vCards]);
  }

  function handleFileAdded(event) {
    const file = event.target.files[0];
    if (!file) return;
    setIsUploading(true);
    setFormLibraryModalOpen(false);
    uploadFile({ file, currentAttachments: [], standard: true }, ({ attachments: uploaded }) => {
      setIsUploading(false);
      if (!onAddAttachments || !uploaded.length) return;
      const trimmedFile = { attachmentUrl: uploaded[0].attachmentUrl, name: uploaded[0].name, type: uploaded[0].type, bytes: uploaded[0].bytes || null };
      onAddAttachments([...currentAttachments, trimmedFile]);
    });
    uploaderInputRef.current.value = null;
  }

  const showAddAttachment = isEditMode && onAddAttachments;
  const showFormLibrary = isRhinoformEnabled || isOfficeVCardEnabled;

  function handleAddAttachmentClick() {
    if (showFormLibrary) {
      setFormLibraryModalOpen(true);
    } else {
      uploaderInputRef.current.click();
    }
  }

  const selectedFormsForModal = currentAttachments
    .filter((a) => a.formId)
    .map((a) => ({ attachmentUrl: a.formId, name: a.title, type: 'form', bytes: 0 }));

  const selectedVCardsForModal = currentAttachments.filter((a) => a.type === VCARD_FILE_TYPE);

  const isPhoneMissing = phones && phones.length > 0 && !phoneId;
  const hasContent = messageText.trim() || currentAttachments.length > 0;
  const isConfirmDisabled = !selectedDate || !selectedTime || (isEditMode && !hasContent) || isPhoneMissing || (phones && phones.length === 0) || isUploading;

  const phoneDropdownLabel = () => {
    if (!phones || phones.length === 0) return 'No phone available';
    if (phoneId) return formatPhone(phones.find((p) => Number(p.id) === phoneId)?.value || '') || 'Select phone';
    return 'Select phone';
  };

  const selectedTimeLabel = selectedTime
    ? TIME_OPTIONS.find((o) => o.value === selectedTime)?.label || selectedTime
    : 'Time';

  return (
    <>
      <Modal open={isOpen}>
        <ModalHeader title={isEditMode ? 'Edit message' : 'Schedule message'} onClose={onClose} />
        <ModalBody>
          {isEditMode && (
            <Textarea
              name="messageText"
              label="Message"
              initialValue={messageText}
              onChange={(name, value) => setMessageText(value)}
            />
          )}
          {isEditMode && (
            <div className="u-m-b">
              <p className="u-text-small u-text-muted u-m-b-small">Attachments</p>
              {currentAttachments.length > 0 && (
                <UtilityList className="u-m-b-small">
                  {currentAttachments.map((attachment) => (
                    <UtilityListItem key={attachment.key || attachment.formId || attachment.attachmentUrl} className="u-text-small">
                      <Button
                        reset
                        className="u-text-primary"
                        onClick={() => onRemoveAttachment && onRemoveAttachment(attachment)}
                      >
                        <Icon icon={getAttachmentIcon(attachment)} />
                        &nbsp;{formatFilename(attachment.formId ? attachment.title : attachment.name)}&nbsp;&nbsp;
                        <span className="u-text-muted"><Icon size="small" icon="close" /></span>
                      </Button>
                    </UtilityListItem>
                  ))}
                </UtilityList>
              )}
              {showAddAttachment && (
                <>
                  <input
                    className="u-sr-only"
                    onChange={handleFileAdded}
                    ref={uploaderInputRef}
                    type="file"
                  />
                  <Button
                    size="small"
                    type="link"
                    loading={isUploading}
                    onClick={handleAddAttachmentClick}
                  >
                    <Icon icon="plus" />&nbsp;Add attachment
                  </Button>
                </>
              )}
            </div>
          )}
          {phones && (
            <div className="u-m-b">
              <p className="u-text-small u-text-muted u-m-b-small">To Phone</p>
              <Dropdown
                label={phoneDropdownLabel()}
                type="outline"
                block
                disabled={phones.length === 0}
              >
                {phones.map((phone) => (
                  <DropdownMenuItem
                    key={phone.id}
                    id={String(phone.id)}
                    label={formatPhone(phone.value || '') || phone.value}
                    onClick={() => setPhoneId(Number(phone.id))}
                    active={Number(phone.id) === phoneId}
                  />
                ))}
              </Dropdown>
            </div>
          )}
          <p className="u-text-muted u-text-small u-m-b u-m-t">Time zone: {tzLabel}</p>
          <div className="row">
            <div className="column-8@small">
              <Date
                name="scheduledDate"
                selected={selectedDate ? moment(selectedDate, DATE_FORMAT_DEFAULT) : null}
                onChange={handleDateChange}
                minDate={today}
                explanationMessage=""
              />
            </div>
            <div className="column-4@small">
              <Dropdown
                label={selectedTimeLabel}
                type="outline"
                block
                lockLabel
              >
                {availableTimeOptions.map((opt) => (
                  <DropdownMenuItem
                    key={opt.value}
                    id={opt.value}
                    label={opt.label}
                    onClick={() => handleTimeSelect(opt)}
                    active={opt.value === selectedTime}
                  />
                ))}
              </Dropdown>
            </div>
          </div>
          {dateError && <p className="u-text-danger u-text-small u-m-t-small">{dateError}</p>}
        </ModalBody>
        <ModalFooter>
          <UtilityInlineGrid align="between">
            <Button type="link-muted" className="u-p-l-0" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleConfirm}
              disabled={isConfirmDisabled}
            >
              {isEditMode ? 'Update Schedule' : 'Schedule a Message'}
            </Button>
          </UtilityInlineGrid>
        </ModalFooter>
      </Modal>
      {showAddAttachment && showFormLibrary && (
        <FormLibraryModal
          open={formLibraryModalOpen}
          closeModal={() => setFormLibraryModalOpen(false)}
          handleFormAppend={handleFormAppend}
          handleVCardAppend={handleVCardAppend}
          handleAddFile={handleFileAdded}
          isMultiSelect
          selectedForms={selectedFormsForModal}
          selectedVCards={selectedVCardsForModal}
        />
      )}
    </>
  );
};

ScheduledMessageModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  initialScheduledAt: PropTypes.string,
  initialMessageText: PropTypes.string,
  phones: PropTypes.array,
  selectedPhoneId: PropTypes.number,
  attachments: PropTypes.array,
  onRemoveAttachment: PropTypes.func,
  onAddAttachments: PropTypes.func,
  isRhinoformEnabled: PropTypes.bool,
  isOfficeVCardEnabled: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  isRhinoformEnabled: !!(state.form.org?.isRhinoformEnabled && getLoggedInUserPermissionNames(state)?.includes(FORM_VIEW)),
  isOfficeVCardEnabled: !!getCurrentOrg(state)?.isOfficeVCardEnabled,
});

export default connect(mapStateToProps)(ScheduledMessageModal);
